import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';

const navLinks = [
  { label: 'Products', href: '/products' },
  { label: 'About', href: '/about' },
  { label: 'FAQ', href: '/faq' },
];

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (href) =>
    location.pathname === href || (href === '/products' && location.pathname.startsWith('/products'));

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 animate-km-nav-mount transition-all duration-300 ${
        scrolled ? 'bg-km-gray-1/95 backdrop-blur-md border-b border-km-gray-3' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center cursor-pointer">
            <img
              src="/brand/kingmakers-logo-hybrid.png"
              alt="King Makers"
              className="h-10 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`font-inter text-sm tracking-wide cursor-pointer transition-colors duration-200 ${
                  isActive(link.href) ? 'text-km-gold font-medium' : 'text-km-gray-5 hover:text-km-gold'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="inline-flex items-center justify-center px-6 py-2.5 rounded-[10px] bg-km-gold text-km-gray-1 font-inter font-medium text-sm tracking-wide hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer"
            >
              Get in Touch
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 cursor-pointer text-km-white"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-km-gray-2 border-t border-km-gray-3">
          <div className="px-6 py-8 space-y-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className={`block font-inter text-base cursor-pointer ${
                  isActive(link.href) ? 'text-km-gold' : 'text-km-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-contact"
              className="flex items-center justify-center px-6 py-2.5 rounded-[10px] bg-km-gold text-km-gray-1 font-inter font-medium text-sm tracking-wide hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer w-full text-center"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

function Footer() {
  return (
    <footer data-testid="main-footer" className="relative bg-km-dark-vertical border-t border-km-gray-3">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
          <div className="md:col-span-1">
            <Link to="/" className="inline-block cursor-pointer">
              <img
                src="/brand/kingmakers-logo-hybrid.png"
                alt="King Makers"
                className="h-9 w-auto"
              />
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-km-gray-5 font-inter">
              Decision-grade clarity for founders and professionals.
            </p>
            <p className="mt-3 text-xs text-km-gray-5 font-inter">
              Toronto, ON, Canada
            </p>
          </div>

          <div>
            <h4 className="font-inter text-xs font-semibold tracking-[0.2em] uppercase text-km-gray-5 mb-5">Products</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/products/high-stakes-narrative-pack" className="font-inter text-sm text-km-white/70 hover:text-km-white transition-colors cursor-pointer">
                  High-Stakes Narrative Pack
                </Link>
              </li>
              <li><Link to="/products/decision-architecture-system" className="font-inter text-sm text-km-gray-5 cursor-pointer">Decision Architecture <span className="text-km-gray-5 text-xs ml-1">Soon</span></Link></li>
              <li><Link to="/products/org-clarity-system" className="font-inter text-sm text-km-gray-5 cursor-pointer">Org Clarity System <span className="text-km-gray-5 text-xs ml-1">Soon</span></Link></li>
              <li><Link to="/products/founder-time-leverage-system" className="font-inter text-sm text-km-gray-5 cursor-pointer">Time-Leverage System <span className="text-km-gray-5 text-xs ml-1">Soon</span></Link></li>
              <li><Link to="/products/career-leverage-calculator" className="font-inter text-sm text-km-gray-5 cursor-pointer">Career Leverage Calculator <span className="text-km-gray-5 text-xs ml-1">Soon</span></Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-inter text-xs font-semibold tracking-[0.2em] uppercase text-km-gray-5 mb-5">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="font-inter text-sm text-km-white/70 hover:text-km-white transition-colors cursor-pointer">About</Link></li>
              <li><Link to="/faq" className="font-inter text-sm text-km-white/70 hover:text-km-white transition-colors cursor-pointer">FAQ</Link></li>
              <li><Link to="/contact" className="font-inter text-sm text-km-white/70 hover:text-km-white transition-colors cursor-pointer">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-inter text-xs font-semibold tracking-[0.2em] uppercase text-km-gray-5 mb-5">Connect</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:hello@kingmakersinc.ca" className="font-inter text-sm text-km-white/70 hover:text-km-white transition-colors cursor-pointer">
                  hello@kingmakersinc.ca
                </a>
              </li>
              <li>
                <a href="https://kingmakersinc.ca" target="_blank" rel="noopener noreferrer" className="font-inter text-sm text-km-white/70 hover:text-km-white transition-colors cursor-pointer inline-flex items-center gap-1.5">
                  kingmakersinc.ca <ArrowUpRight className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-km-gray-3 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-inter text-xs text-km-gray-5">
            &copy; {new Date().getFullYear()} King Makers. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="font-inter text-xs text-km-gray-5 hover:text-km-white transition-colors cursor-pointer">Privacy</Link>
            <Link to="/terms" className="font-inter text-xs text-km-gray-5 hover:text-km-white transition-colors cursor-pointer">Terms</Link>
            <Link to="/refund-policy" className="font-inter text-xs text-km-gray-5 hover:text-km-white transition-colors cursor-pointer">Refund Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="relative min-h-screen flex flex-col bg-km-gray-1">
      {/* Decorative top-left blur per DESIGN-SYSTEM.md §10 (global layout blur placement) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-[200px] -top-[200px] h-[500px] w-[500px] rounded-[50px] bg-km-white/10 blur-[100px]"
      />
      <Navbar />
      <main className="relative flex-1 pt-20">{children}</main>
      <Footer />
    </div>
  );
}
